import { useEffect, useMemo, useState } from "react";
import { useSearch } from "./useSearch";
import { OpenDataTypeEnum } from "@/src/features/api/openData";
import type { ThemeMarketType } from "@/src/const";
import { StockMarketType } from "@/src/const";
interface UseThemeSearchProps {
  /** 市场类型：crypto -> subType=TOKEN；us-stock/hk-stock -> subType=STOCK，接口 market=US/HK */
  market: ThemeMarketType;
  /** 搜索关键词 */
  searchTerm: string;
  /** 是否启用搜索（用于控制是否显示结果） */
  enabled?: boolean;
}

/**
 * 主题搜索 Hook（基于 /search 接口）
 * - 使用 useSearch，仅搜索 THEME 类型
 * - 支持根据市场类型区分股票市场（US/HK）
 */
export function useThemeSearch({
  market,
  searchTerm,
  enabled = true,
}: UseThemeSearchProps) {
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const isSearching = enabled && debouncedSearchTerm.length > 0;

  // 从 market 推导接口参数：subType（crypto->TOKEN / stock->STOCK）、market（US/HK，仅 stock 时传）
  const { apiSubType, apiMarket } = useMemo(() => {
    const subType =
      market === "crypto" ? OpenDataTypeEnum.TOKEN : OpenDataTypeEnum.STOCK;
    const m =
      market === "crypto"
        ? undefined
        : market.includes("us-stock")
          ? StockMarketType.US
          : market.includes("hk-stock")
            ? StockMarketType.HK
            : undefined;
    return { apiSubType: subType, apiMarket: m };
  }, [market]);

  const { data: searchRows, isLoading } = useSearch({
    keyword: debouncedSearchTerm,
    type: OpenDataTypeEnum.THEME,
    subType: apiSubType,
    market: apiMarket,
    limit: 20,
    enabled: isSearching,
  });

  const results = useMemo(() => {
    if (!isSearching || !searchRows?.length) {
      return [];
    }
    return searchRows.map((row) => ({
      id: row.id,
      slug: row.short_name,
      theme: row.name,
      desc: row.desc,
      image: row.image,
    }));
  }, [searchRows, isSearching]);

  return {
    /** 搜索结果 */
    results,
    /** 是否有结果 */
    hasResults: results.length > 0,
    /** 是否正在搜索 */
    isSearching,
    /** 是否正在加载 */
    isLoading: isLoading && isSearching,
  };
}
